import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Maximize2, X, ZoomIn, ZoomOut } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  propsDataLocationToPathAndValue,
  TemplateContext,
  useStore,
} from '@ea-lab/reactive-json'
import { useEscapeClose } from '../hooks/useEscapeClose'
import { useScrimDismiss } from '../hooks/useScrimDismiss'
import useMediaQuery from '../hooks/useMediaQuery'

/**
 * ImageLightbox — visionneuse d'images en pop-in, pilotée par le YAML.
 *
 * POURQUOI DU JS. Ouvrir une pop-in est déclaratif (`setData` + `Modal`), mais
 * PASSER D'UNE IMAGE À L'AUTRE ne l'est pas : il faut une arithmétique d'index
 * (i±1, cyclique et bornée) qu'aucune réaction reactive-json n'exprime —
 * `setData` écrit une valeur, `addData` empile, aucune n'incrémente. S'y
 * ajoutent trois gestes de navigateur sans équivalent YAML : les flèches
 * clavier, le piège de focus d'un dialogue modal (WCAG 2.1.2) et la
 * restauration du focus à la fermeture.
 *
 * Le ZOOM tombe sous la même règle : borner un facteur, garder sous le curseur le
 * point qu'on y visait (une composition de transformées), borner le décalage au
 * débord réel et poser un écouteur `wheel` NON PASSIF sont des calculs et une API
 * du navigateur — le YAML écrit des valeurs, il ne multiplie rien et n'attache
 * aucun écouteur.
 *
 * CE QUI RESTE AU YAML : QUELLES images (la collection), LAQUELLE est ouverte
 * (une simple donnée écrite par un `setData` au clic), et tout le rendu de la
 * liste. Le composant ne connaît ni les tâches, ni les fichiers Drupal : il lit
 * une collection d'objets et trois clés configurables.
 *
 * Usage YAML :
 *   # Dans la liste : le clic ne fait qu'écrire l'URL de l'image à ouvrir.
 *   - type: button
 *     attributes: { type: button }
 *     content: …
 *     actions:
 *       - { what: setData, on: click, path: ~~.lightboxUrl, value: ~.data.url }
 *
 *   # Une fois, à côté de la liste :
 *   - type: ImageLightbox
 *     dataLocation: ~~.taskFiles       # collection (items plats OU syncables)
 *     openLocation: ~~.lightboxUrl     # URL ouverte ("" = pop-in fermée)
 *     urlKey: url                      # optionnel (défaut : url)
 *     labelKey: filename               # optionnel (défaut : filename)
 *     mimeKey: filemime                # optionnel (défaut : filemime)
 *     label: "Aperçu des pièces jointes"   # nom accessible du dialogue
 *
 * POURQUOI PAS L'ÉLÉMENT `Modal`. Il rend son corps INLINE (pas de portail) à
 * partir d'un sous-arbre YAML (`View` sur `props.body`), au z-50 du DS, et sans
 * `role="dialog"`/`aria-modal` ni piège de focus. Ici le corps dépend de l'index
 * courant (donc du JS), doit couvrir tout le chrome (`modal-scrim-full`) et le
 * dialogue doit être modal au sens ARIA. Le jour où `Modal` gagne ces trois
 * choses, cette coquille pourra fondre dedans.
 *
 * ABONNEMENT AU MAGASIN. Un composant monté en plugin lit le store en accès NON
 * RÉACTIF : avec `evaluateTemplateValue` seul, la pop-in ne se rouvrirait jamais
 * après le premier rendu, sans la moindre erreur. On s'abonne donc au DataStore
 * comme le moteur lui-même (`View` : `useSyncExternalStore` + `store.subscribe`),
 * et l'instantané est une CHAÎNE — un objet neuf à chaque appel ferait boucler
 * `useSyncExternalStore`.
 */

/** Item de collection : forme Syncable Object `{ data: {…} }` ou objet plat. */
const rowOf = (item) =>
  item && typeof item === 'object' && item.data && typeof item.data === 'object' ? item.data : item

/** Repli quand la collection ne porte pas de type MIME (liste de simples URLs). */
const IMAGE_EXTENSION = /\.(apng|avif|bmp|gif|ico|jpe?g|jfif|png|svg|webp)(\?|#|$)/i

/** Zoom : 1 = image entière (`object-contain`), au-delà = agrandissement. */
const ZOOM_MIN = 1
const ZOOM_MAX = 8
const ZOOM_STEP = 1.4
/** Niveau du double-clic / du bouton « loupe » quand on part de l'image entière. */
const ZOOM_DOUBLE = 2.5
/** Pas du déplacement au clavier, en pixels d'écran. */
const PAN_STEP = 60

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z))

/**
 * Chemin du MAGASIN pour un chemin de données reactive-json.
 *
 * `~~.taskFiles` se résout en `data.taskFiles` (le `templatePath` d'un root est
 * "data"), mais le DataStore, lui, est indexé SANS ce préfixe : `updateData` le
 * retire avant d'écrire (`ReactiveJsonRoot` : `data.x` → `x`). Un
 * `store.get('data.taskFiles')` rend donc `undefined` — silencieusement, une
 * collection vide et une pop-in qui ne s'ouvre jamais. Même normalisation ici.
 */
const toStorePath = (p) => (!p || p === 'data' ? '' : p.startsWith('data.') ? p.substring(5) : p)

export const ImageLightbox = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const store = useStore()

  const urlKey = props.urlKey || 'url'
  const labelKey = props.labelKey || 'filename'
  const mimeKey = props.mimeKey || 'filemime'

  // Chemins POINTÉS du magasin (`~~.taskFiles` → `taskFiles`) : ce sont eux que
  // `store.subscribe` et `updateData` attendent.
  const { formDataPath: filesPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const { formDataPath: openPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.openLocation,
    defaultValue: '',
    globalDataContext,
    templateContext,
  })

  const filesStorePath = toStorePath(filesPath)
  const openStorePath = toStorePath(openPath)

  const subscribe = useCallback((onChange) => {
    const unsubs = [filesStorePath, openStorePath]
      .filter((p) => typeof p === 'string' && p !== '')
      .map((p) => store.subscribe(p, onChange))
    return () => unsubs.forEach((u) => u())
  }, [store, filesStorePath, openStorePath])

  const getSnapshot = useCallback(
    () => JSON.stringify([store.get(filesStorePath) ?? [], store.get(openStorePath) ?? '']),
    [store, filesStorePath, openStorePath],
  )

  const snapshot = useSyncExternalStore(subscribe, getSnapshot)

  const { images, openUrl } = useMemo(() => {
    const [rawFiles, rawOpen] = JSON.parse(snapshot)
    const list = Array.isArray(rawFiles) ? rawFiles : Object.values(rawFiles ?? {})
    const images = list
      .map(rowOf)
      .filter((row) => row && typeof row === 'object')
      .map((row) => ({
        url: row[urlKey] ?? '',
        label: String(row[labelKey] ?? ''),
        mime: String(row[mimeKey] ?? ''),
      }))
      .filter(({ url, label, mime }) =>
        url && (mime ? mime.startsWith('image/') : IMAGE_EXTENSION.test(label || url)))
    return { images, openUrl: typeof rawOpen === 'string' ? rawOpen : '' }
  }, [snapshot, urlKey, labelKey, mimeKey])

  const index = openUrl ? images.findIndex((img) => img.url === openUrl) : -1
  const isOpen = index >= 0

  const close = useCallback(() => {
    globalDataContext?.updateData('', openPath)
  }, [globalDataContext, openPath])

  // L'image ouverte a disparu de la collection (suppression, rechargement) : la
  // pop-in n'a plus rien à montrer, on referme plutôt que d'afficher un cadre vide.
  useEffect(() => {
    if (openUrl && index < 0 && images.length > 0) close()
  }, [openUrl, index, images.length, close])

  const go = useCallback((delta) => {
    if (images.length < 2 || index < 0) return
    const next = (index + delta + images.length) % images.length
    globalDataContext?.updateData(images[next].url, openPath)
  }, [images, index, globalDataContext, openPath])

  const scrim = useScrimDismiss(close)
  useEscapeClose(close, isOpen)

  // Focus : le dialogue le prend à l'ouverture, le déclencheur le récupère à la
  // fermeture (WCAG 2.4.3), et le Tab tourne en rond dans la pop-in (2.1.2).
  const dialogRef = useRef(null)
  const restoreRef = useRef(null)
  useEffect(() => {
    if (!isOpen) return
    restoreRef.current = document.activeElement
    dialogRef.current?.focus()
    return () => {
      const target = restoreRef.current
      restoreRef.current = null
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus()
    }
  }, [isOpen])

  // ── Zoom et déplacement ─────────────────────────────────────────────────
  // `zoom` est un facteur appliqué en `transform` à l'image déjà mise à l'échelle
  // par `object-contain` : 1 = l'image entière, quelle que soit sa définition. Le
  // décalage est en pixels d'ÉCRAN (donc posé AVANT l'échelle dans la
  // transformée), ce qui rend le déplacement à la souris exact au pixel près.
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const viewportRef = useRef(null)
  const imgRef = useRef(null)
  const dragRef = useRef(null)
  const pointersRef = useRef(new Map())
  const pinchRef = useRef(null)

  // Miroirs du zoom et du décalage. Les gestes (molette, pincement) calculent la
  // cible à partir de la valeur COURANTE : la lire dans un updater de `setState`
  // marcherait, mais un updater doit rester pur — React le rejoue (StrictMode,
  // rebase concurrent) et un calcul non idempotent comme le recentrage sur le
  // curseur s'appliquerait deux fois, faisant sauter l'image.
  const zoomRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  zoomRef.current = zoom
  offsetRef.current = offset

  /** Purge l'état de geste : un pointeur relâché hors du cadre ne revient jamais. */
  const endGestures = useCallback(() => {
    dragRef.current = null
    pinchRef.current = null
    pointersRef.current.clear()
    setIsDragging(false)
  }, [])

  /**
   * Borne le décalage pour que l'image ne puisse pas être poussée hors du cadre.
   * La marge disponible est la moitié du débord (l'image est centrée) ; sans
   * débord, le seul décalage licite est zéro.
   */
  const clampOffset = useCallback((x, y, z) => {
    const box = viewportRef.current?.getBoundingClientRect()
    const img = imgRef.current
    if (!box || !img) return { x, y }
    const maxX = Math.max(0, (img.clientWidth * z - box.width) / 2)
    const maxY = Math.max(0, (img.clientHeight * z - box.height) / 2)
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    }
  }, [])

  /**
   * Passe au facteur `next` (valeur, ou fonction du facteur courant), en gardant
   * sous le curseur le point de l'image qui s'y trouvait (`clientX/Y`). Sans
   * origine, le zoom se fait sur le centre du cadre.
   */
  const zoomTo = useCallback((next, clientX, clientY) => {
    const current = zoomRef.current
    const target = clampZoom(typeof next === 'function' ? next(current) : next)
    if (target === current) return
    const o = offsetRef.current
    const box = viewportRef.current?.getBoundingClientRect()
    const k = target / current
    const nextOffset = (!box || clientX == null)
      ? clampOffset(o.x * k, o.y * k, target)
      // Position du curseur par rapport au CENTRE du cadre.
      : (() => {
        const px = clientX - (box.left + box.width / 2)
        const py = clientY - (box.top + box.height / 2)
        return clampOffset(px - k * (px - o.x), py - k * (py - o.y), target)
      })()
    zoomRef.current = target
    offsetRef.current = nextOffset
    setZoom(target)
    setOffset(nextOffset)
  }, [clampOffset])

  const panBy = useCallback((dx, dy) => {
    const o = offsetRef.current
    const next = clampOffset(o.x + dx, o.y + dy, zoomRef.current)
    offsetRef.current = next
    setOffset(next)
  }, [clampOffset])

  const resetZoom = useCallback(() => {
    zoomRef.current = 1
    offsetRef.current = { x: 0, y: 0 }
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  // Changer d'image (ou rouvrir la pop-in) remet la vue à plat : garder le zoom
  // de l'image précédente ferait arriver sur un fragment, sans repère. En
  // `useLayoutEffect` : un `useEffect` laisserait PEINDRE la nouvelle image avec
  // l'ancienne transformée, et la remise à 100 % partirait en transition — on
  // verrait le fragment se dézoomer à chaque navigation.
  useLayoutEffect(() => {
    resetZoom()
    endGestures()
  }, [openUrl, resetZoom, endGestures])

  // Le cadre change de taille (fenêtre redimensionnée, rotation) : un décalage
  // calculé pour l'ancienne taille laisserait l'image hors champ, sans retour.
  useEffect(() => {
    const node = viewportRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const o = offsetRef.current
      const next = clampOffset(o.x, o.y, zoomRef.current)
      offsetRef.current = next
      setOffset(next)
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [clampOffset, isOpen])

  // La molette zoome — d'où un écouteur NON PASSIF posé à la main : React attache
  // `onWheel` en passif à la racine, `preventDefault` y est ignoré (la page
  // défilerait derrière la pop-in) et rien ne le signale. Le facteur est calculé
  // par `zoomTo` depuis la ref : une rafale de crans dans le même lot de rendu ne
  // repart donc pas d'une valeur périmée.
  useEffect(() => {
    const node = viewportRef.current
    if (!node) return
    const onWheel = (e) => {
      e.preventDefault()
      zoomTo((z) => z * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), e.clientX, e.clientY)
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [zoomTo, isOpen])

  // Le cadre est en `touch-action: none` (sans quoi le navigateur préempterait le
  // glisser pour faire défiler) : le pincement natif ne s'y produit donc plus, et
  // c'est à nous de l'assurer — sinon le tactile n'aurait plus que les boutons.
  // D'où le suivi de TOUS les pointeurs : un = déplacement, deux = pincement
  // (l'écart des doigts donne le facteur, leur milieu l'origine).
  const pinchState = () => {
    const [a, b] = [...pointersRef.current.values()]
    return {
      dist: Math.hypot(a.x - b.x, a.y - b.y),
      cx: (a.x + b.x) / 2,
      cy: (a.y + b.y) / 2,
    }
  }

  const startDrag = (pointer) => {
    dragRef.current = { x: pointer.x, y: pointer.y, ox: offsetRef.current.x, oy: offsetRef.current.y }
    setIsDragging(true)
  }

  const onPointerDown = (e) => {
    // Le pavé de déplacement vit DANS le cadre : sans cette garde, le
    // `preventDefault` ci-dessous supprimerait les événements souris de
    // compatibilité (dont le `click`) et ses boutons ne feraient rien — le seul
    // symptôme étant un bouton qui ne répond pas, sans la moindre erreur.
    if (e.target.closest?.('button')) return
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointersRef.current.size === 2) {
      dragRef.current = null
      setIsDragging(false)
      pinchRef.current = { ...pinchState(), zoom: zoomRef.current }
      return
    }
    if (zoomRef.current <= 1 || e.button !== 0) return
    e.preventDefault()
    startDrag({ x: e.clientX, y: e.clientY })
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    const pinch = pinchRef.current
    if (pinch && pointersRef.current.size === 2) {
      const now = pinchState()
      if (pinch.dist > 0) zoomTo(pinch.zoom * (now.dist / pinch.dist), now.cx, now.cy)
      return
    }
    const d = dragRef.current
    if (!d) return
    const next = clampOffset(d.ox + (e.clientX - d.x), d.oy + (e.clientY - d.y), zoomRef.current)
    offsetRef.current = next
    setOffset(next)
  }

  const onPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId)
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    if (pinchRef.current && pointersRef.current.size < 2) {
      pinchRef.current = null
      // Un doigt reste posé après un pincement : il reprend le déplacement,
      // sinon le geste mourrait en silence sous le doigt de l'utilisateur.
      const [rest] = [...pointersRef.current.values()]
      if (rest && zoomRef.current > 1) startDrag(rest)
      return
    }
    if (!dragRef.current) return
    dragRef.current = null
    setIsDragging(false)
  }

  // Le niveau affiché change à CHAQUE cran de molette ou de pincement : le
  // pousser tel quel dans une région live noierait le lecteur d'écran sous des
  // dizaines d'annonces. On n'annonce donc que la valeur STABILISÉE (WCAG 4.1.3
  // demande que le changement soit restitué, pas qu'il le soit à chaque frame).
  const [announcedZoom, setAnnouncedZoom] = useState(1)
  useEffect(() => {
    const t = setTimeout(() => setAnnouncedZoom(zoom), 400)
    return () => clearTimeout(t)
  }, [zoom])

  // Le zoom au bouton/clavier est animé ; sous « mouvement réduit », il ne l'est pas.
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const onDoubleClick = (e) => {
    if (zoomRef.current > 1) resetZoom()
    else zoomTo(ZOOM_DOUBLE, e.clientX, e.clientY)
  }

  // Précharge les voisines : la navigation doit être immédiate, pas un flash blanc.
  useEffect(() => {
    if (!isOpen || images.length < 2) return
    const around = [images[(index + 1) % images.length], images[(index - 1 + images.length) % images.length]]
    around.forEach(({ url }) => { const img = new Image(); img.src = url })
  }, [isOpen, index, images])

  const onKeyDown = (e) => {
    // Zoom au clavier : les mêmes touches que partout ailleurs (+ / - / 0).
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo((z) => z * ZOOM_STEP); return }
    if (e.key === '-') { e.preventDefault(); zoomTo((z) => z / ZOOM_STEP); return }
    if (e.key === '0') { e.preventDefault(); resetZoom(); return }
    // Une fois ZOOMÉ, les flèches déplacent la vue (on ne voit plus qu'un
    // fragment : s'y promener prime). L'image suivante reste atteignable par
    // Page suiv./préc. et par les boutons ‹ ›, qui reçoivent le focus.
    if (zoom > 1 && ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault()
      panBy(
        e.key === 'ArrowLeft' ? PAN_STEP : e.key === 'ArrowRight' ? -PAN_STEP : 0,
        e.key === 'ArrowUp' ? PAN_STEP : e.key === 'ArrowDown' ? -PAN_STEP : 0,
      )
      return
    }
    if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); go(1); return }
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); return }
    if (e.key === 'Home') { e.preventDefault(); if (images.length) globalDataContext?.updateData(images[0].url, openPath); return }
    if (e.key === 'End') { e.preventDefault(); if (images.length) globalDataContext?.updateData(images[images.length - 1].url, openPath); return }
    if (e.key !== 'Tab') return
    const focusables = dialogRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    if (!focusables?.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      e.preventDefault()
      last.focus()
    }
    else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  if (!isOpen) return null

  const current = images[index]
  const many = images.length > 1
  const titleId = `lightbox-title-${openPath.replace(/[^\w-]/g, '-')}`

  return (
    <ActionDependant {...props}>
      {createPortal(
        <div className="modal-scrim modal-scrim-full" {...scrim}>
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            className="modal-panel modal-panel-full"
          >
            <div className="modal-header gap-3">
              {/* Le nom et le rang changent sans que le focus bouge (on reste
                  sur la flèche) : sans région live, la navigation au clavier
                  n'annonce rien du tout — WCAG 4.1.3. */}
              <div className="min-w-0 flex items-baseline gap-2" aria-live="polite">
                <span id={titleId} className="truncate">{current.label || 'Image'}</span>
                {many && (
                  <span className="text-sm font-normal text-slate-500 shrink-0">
                    {index + 1} / {images.length}
                  </span>
                )}
              </div>
              {/* Les boutons de zoom ne sont JAMAIS `disabled` : désactiver
                  l'élément qu'on vient de cliquer lui fait perdre le focus au
                  profit de `body` — hors du dialogue, donc plus de raccourcis,
                  plus de piège de Tab, et le Tab suivant repartirait derrière le
                  scrim (WCAG 2.1.2 / 2.4.3). Ils restent focusables, annoncés
                  indisponibles, et leur action est simplement sans effet. */}
              <div className="flex items-center gap-1 shrink-0" role="group" aria-label="Zoom">
                <button
                  type="button"
                  className={`btn btn-secondary btn-sm${zoom <= ZOOM_MIN ? ' opacity-50' : ''}`}
                  onClick={() => zoomTo((z) => z / ZOOM_STEP)}
                  aria-disabled={zoom <= ZOOM_MIN}
                  aria-label="Dézoomer"
                  aria-keyshortcuts="Minus"
                  title="Dézoomer (touche -)"
                >
                  <ZoomOut size={16} aria-hidden="true" />
                </button>
                {/* Valeur VISIBLE (mise à jour à chaque cran) ; sa restitution
                    vocale passe par la région live stabilisée, plus bas. */}
                <span className="text-xs font-normal text-slate-500 tabular-nums w-14 text-center" aria-hidden="true">
                  {Math.round(zoom * 100)} %
                </span>
                <button
                  type="button"
                  className={`btn btn-secondary btn-sm${zoom >= ZOOM_MAX ? ' opacity-50' : ''}`}
                  onClick={() => zoomTo((z) => (z === 1 ? ZOOM_DOUBLE : z * ZOOM_STEP))}
                  aria-disabled={zoom >= ZOOM_MAX}
                  aria-label="Zoomer"
                  aria-keyshortcuts="Plus"
                  title="Zoomer (molette, pincement, double-clic, touche +)"
                >
                  <ZoomIn size={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`btn btn-secondary btn-sm${zoom === 1 && offset.x === 0 && offset.y === 0 ? ' opacity-50' : ''}`}
                  onClick={resetZoom}
                  aria-disabled={zoom === 1 && offset.x === 0 && offset.y === 0}
                  aria-label="Afficher l'image entière"
                  aria-keyshortcuts="0"
                  title="Afficher l'image entière (touche 0)"
                >
                  <Maximize2 size={16} aria-hidden="true" />
                </button>
                <button type="button" className="btn btn-secondary btn-sm ml-2" onClick={close}>
                  <X size={16} aria-hidden="true" />
                  Fermer
                </button>
              </div>
            </div>

            {/* Restitution vocale : le niveau stabilisé, et le mode des flèches,
                qui change au-delà de 100 % — sans quoi un utilisateur clavier
                croirait la galerie bloquée. */}
            <p className="sr-only" role="status">
              Zoom : {Math.round(announcedZoom * 100)} %.
              {announcedZoom > 1
                ? " Les flèches déplacent la vue ; Page précédente et Page suivante changent d'image."
                : ' Les flèches changent d’image.'}
            </p>

            {/* L'image est dans un conteneur `flex-1 min-w-0` : sans lui, un
                `max-w-full` calculé sur toute la rangée pousse les deux boutons
                hors du panneau (`overflow: hidden`) et la navigation disparaît. */}
            <div className="modal-body flex-1 min-h-0 flex items-center gap-3 bg-slate-100">
              {many && (
                <button
                  type="button"
                  className="btn btn-secondary shrink-0"
                  onClick={() => go(-1)}
                  aria-label="Image précédente"
                >
                  <ChevronLeft size={20} aria-hidden="true" />
                </button>
              )}
              {/* Le cadre DÉCOUPE (`overflow-hidden`) : une fois zoomée, l'image
                  déborde et ne doit pas passer sous les flèches. `touch-none`
                  laisse le glisser au pointeur plutôt qu'au défilement tactile. */}
              <div
                ref={viewportRef}
                className="relative flex-1 min-w-0 h-full flex items-center justify-center overflow-hidden touch-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onLostPointerCapture={onPointerUp}
                onDoubleClick={onDoubleClick}
              >
                <img
                  ref={imgRef}
                  src={current.url}
                  alt={current.label || 'Image en aperçu'}
                  className="max-h-full max-w-full object-contain select-none"
                  draggable={false}
                  style={{
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                    transition: isDragging || reducedMotion ? 'none' : 'transform 120ms ease-out',
                    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  }}
                />
                {/* Déplacement SANS GLISSER (WCAG 2.5.7) : le glisser est le
                    geste naturel à la souris, mais il ne peut pas être le SEUL
                    moyen d'atteindre le reste de l'image — d'autant que le cadre
                    neutralise le défilement natif (`touch-action: none`). Ce
                    pavé n'apparaît qu'une fois zoomé, là où il y a un débord. */}
                {zoom > 1 && (
                  <div
                    className="absolute bottom-3 right-3 grid grid-cols-3 gap-1 p-1 rounded-xl bg-white/90 shadow-sm border border-slate-200"
                    role="group"
                    aria-label="Déplacer la vue"
                  >
                    <span />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => panBy(0, PAN_STEP)} aria-label="Déplacer vers le haut">
                      <ChevronUp size={14} aria-hidden="true" />
                    </button>
                    <span />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => panBy(PAN_STEP, 0)} aria-label="Déplacer vers la gauche">
                      <ChevronLeft size={14} aria-hidden="true" />
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={resetZoom} aria-label="Recentrer et afficher l'image entière">
                      <Maximize2 size={14} aria-hidden="true" />
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => panBy(-PAN_STEP, 0)} aria-label="Déplacer vers la droite">
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                    <span />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => panBy(0, -PAN_STEP)} aria-label="Déplacer vers le bas">
                      <ChevronDown size={14} aria-hidden="true" />
                    </button>
                    <span />
                  </div>
                )}
              </div>
              {many && (
                <button
                  type="button"
                  className="btn btn-secondary shrink-0"
                  onClick={() => go(1)}
                  aria-label="Image suivante"
                >
                  <ChevronRight size={20} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ActionDependant>
  )
}
